import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { useCart, useStore } from '../lib/store'
import { Img, Loading, Shell } from '../components/Layout'
import { usePricedCart, Row } from './Cart'

type Fields = Record<string, string>

const REQUIRED: [keyof Fields & string, string][] = [
  ['customerName', 'Full name'],
  ['email', 'Email address'],
  ['address1', 'Address'],
  ['city', 'Town or city'],
  ['postcode', 'Postcode'],
]

/**
 * Declared at module level, not inside `Checkout`: a component defined in a
 * render is a new type every render, so React would unmount and remount the
 * input on each keystroke and focus would be lost after every letter.
 */
function Field({
  name,
  label,
  form,
  errors,
  onChange,
  type = 'text',
  required = false,
  area = false,
}: {
  name: string
  label: string
  form: Fields
  errors: Fields
  onChange: (name: string, value: string) => void
  type?: string
  required?: boolean
  area?: boolean
}) {
  const id = `co-${name}`
  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {required && ' *'}
      </label>
      {area ? (
        <textarea id={id} value={form[name]} onChange={(e) => onChange(name, e.target.value)} maxLength={1000} />
      ) : (
        <input
          id={id}
          name={name}
          type={type}
          value={form[name]}
          onChange={(e) => onChange(name, e.target.value)}
          autoComplete={AUTOCOMPLETE[name]}
          aria-invalid={errors[name] ? true : undefined}
        />
      )}
      {errors[name] && <span className="field-error">{errors[name]}</span>}
    </div>
  )
}

export default function Checkout() {
  const { items, clear } = useCart()
  const { money, user, settings } = useStore()
  const navigate = useNavigate()
  const [params] = useSearchParams()

  // The code being priced. `codeInput` is what is typed; it is only priced
  // when applied, so the bag is not re-priced on every keystroke.
  const [code, setCode] = useState('')
  const [codeInput, setCodeInput] = useState('')
  const { priced, loading } = usePricedCart(code)

  // Stripe sends a shopper who backs out to /checkout?cancelled=<reference>.
  const cancelled = params.get('cancelled')
  const payments = settings?.payments
  const byCard = payments === 'card'
  const unavailable = payments === 'unavailable'

  const [form, setForm] = useState<Fields>({
    customerName: user?.name ?? '',
    email: user?.email ?? '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    country: settings?.countryLabel ?? 'United Kingdom',
    notes: '',
  })
  const [fieldErrors, setFieldErrors] = useState<Fields>({})
  const [error, setError] = useState('')
  const [problems, setProblems] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const onChange = (name: string, value: string) => setForm((f) => ({ ...f, [name]: value }))
  const fieldProps = { form, errors: fieldErrors, onChange }

  const codeError = code && priced?.codeError ? priced.codeError : fieldErrors.code

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setProblems([])
    setFieldErrors({})

    const missing: Fields = {}
    for (const [key, label] of REQUIRED) if (!form[key]?.trim()) missing[key] = `${label} is required.`
    if (Object.keys(missing).length) {
      setFieldErrors(missing)
      return
    }

    setBusy(true)
    try {
      // Only a code the server accepted is sent; the server re-checks it anyway.
      const result = await api.checkout({ ...form, items, code: priced?.code ?? '' })

      // Stripe's hosted payment page. The bag stays as it is until the order
      // page confirms payment, so backing out does not lose it — and `busy`
      // stays set, because re-enabling the button mid-redirect would let a
      // second click place a second order.
      if (result.checkoutUrl) {
        window.location.assign(result.checkoutUrl)
        return
      }

      // Local development only (`payments: 'invoice'`): no payment step.
      clear()
      navigate(`/order/${result.reference}?email=${encodeURIComponent(form.email)}`, { replace: true })
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
        if (err.problems) setProblems(err.problems)
        if (err.fields) {
          setFieldErrors(Object.fromEntries(err.fields.map((f) => [f.path, f.message])))
        }
      } else {
        setError('Could not place your order. Please try again.')
      }
      setBusy(false)
    }
  }

  if (items.length === 0) {
    return (
      <Shell>
        <section className="section" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
          <h1 className="display h-md">Your bag is empty</h1>
          <Link to="/shop" className="btn">
            Shop the range
          </Link>
        </section>
      </Shell>
    )
  }

  return (
    <Shell>
      <section className="section">
        <h1 className="display h-md" style={{ textAlign: 'center', marginBottom: 'clamp(24px,3vw,40px)' }}>
          Checkout
        </h1>

        <form onSubmit={submit} className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(260px,1fr)', gap: 'clamp(20px,3vw,44px)', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {!user && (
              <p className="small" style={{ margin: 0 }}>
                Checking out as a guest. <Link to="/login" style={{ textDecoration: 'underline' }}>Sign in</Link> to save this order to your account.
              </p>
            )}

            {unavailable && (
              <div className="notice notice-error">
                Online payment is temporarily unavailable, so we cannot take orders right now. Nothing has been charged — please try again shortly.
              </div>
            )}

            <h2 style={{ margin: 0, fontSize: 10.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--muted)' }}>Contact</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14 }}>
              <Field name="customerName" label="Full name" required {...fieldProps} />
              <Field name="email" label="Email address" type="email" required {...fieldProps} />
              <Field name="phone" label="Phone (optional)" type="tel" {...fieldProps} />
            </div>

            <h2 style={{ margin: '10px 0 0', fontSize: 10.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--muted)' }}>Delivery address</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 14 }}>
              <Field name="address1" label="Address line 1" required {...fieldProps} />
              <Field name="address2" label="Address line 2 (optional)" {...fieldProps} />
              <Field name="city" label="Town or city" required {...fieldProps} />
              <Field name="postcode" label="Postcode" required {...fieldProps} />
              <Field name="country" label="Country" {...fieldProps} />
            </div>

            <Field name="notes" label="Delivery notes (optional)" area {...fieldProps} />

            {cancelled && !error && (
              <div className="notice">
                Payment was not completed, so order {cancelled} has not been placed. Your bag is untouched — you can try again below.
              </div>
            )}

            {problems.length > 0 && (
              <div className="notice notice-error">
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            )}
            {error && !problems.length && <div className="notice notice-error">{error}</div>}

            {!unavailable && (
              <p className="small" style={{ margin: 0, lineHeight: 1.7, maxWidth: '60ch' }}>
                {byCard
                  ? 'Your items are reserved while you pay. You will enter your card details on Stripe’s secure payment page — Visa, Mastercard, American Express and Apple Pay are accepted, and card details never reach Lumera. Your confirmation is emailed the moment payment clears.'
                  : 'Development mode: payment is not configured, so this order is placed without a payment step.'}
              </p>
            )}
          </div>

          <aside style={{ background: 'var(--stone)', padding: 'clamp(20px,2.4vw,30px)', display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 20 }}>
            <h2 style={{ margin: 0, fontSize: 10.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--muted)' }}>Your order</h2>

            {loading && !priced ? (
              <Loading label="Pricing" />
            ) : (
              <>
                {priced?.items.map((l) => (
                  <div key={l.slug} style={{ display: 'grid', gridTemplateColumns: '48px minmax(0,1fr) auto', gap: 10, alignItems: 'center' }}>
                    <span style={{ display: 'block', aspectRatio: '4 / 5', background: 'var(--stone-2)', overflow: 'hidden' }}>
                      <Img src={l.image} alt={l.name} label="" />
                    </span>
                    <span style={{ fontSize: 12, lineHeight: 1.45 }}>
                      {l.name}
                      <br />
                      <span className="small">× {l.qty}</span>
                    </span>
                    <span style={{ fontSize: 12.5 }}>{money(l.linePrice)}</span>
                  </div>
                ))}

                <div className="field" style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14 }}>
                  <label htmlFor="co-code">Ambassador or discount code</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      id="co-code"
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value)}
                      onKeyDown={(e) => {
                        // Enter applies the code rather than submitting the order.
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          setCode(codeInput.trim().toUpperCase())
                        }
                      }}
                      autoCapitalize="characters"
                      autoComplete="off"
                      maxLength={40}
                      aria-invalid={codeError ? true : undefined}
                      style={{ flex: 1, minWidth: 0, textTransform: 'uppercase' }}
                    />
                    {priced?.code && priced.code === codeInput.trim().toUpperCase() ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => {
                          setCode('')
                          setCodeInput('')
                        }}
                      >
                        Remove
                      </button>
                    ) : (
                      <button type="button" className="btn btn-sm" disabled={!codeInput.trim()} onClick={() => setCode(codeInput.trim().toUpperCase())}>
                        Apply
                      </button>
                    )}
                  </div>
                  {codeError && <span className="field-error">{codeError}</span>}
                </div>

                <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Row label="Subtotal" value={money(priced?.subtotal ?? 0)} />
                  {priced?.discount ? <Row label={`Code ${priced.code}`} value={`−${money(priced.discount)}`} /> : null}
                  <Row label="Delivery" value={priced?.shipping ? money(priced.shipping) : 'Free'} />
                </div>
                <div style={{ borderTop: '1px solid var(--line-2)', paddingTop: 14, display: 'flex', justifyContent: 'space-between', fontSize: 15 }}>
                  <span>Total</span>
                  <span style={{ fontFamily: 'var(--serif)', fontSize: 22 }}>{money(priced?.total ?? 0)}</span>
                </div>
              </>
            )}

            <button
              type="submit"
              className="btn btn-solid"
              style={{ padding: '14px 24px' }}
              disabled={busy || unavailable || !settings || !priced || priced.problems.length > 0}
            >
              {busy ? (byCard ? 'Taking you to payment…' : 'Placing order…') : byCard ? 'Continue to payment' : 'Place order'}
            </button>
            <p style={{ margin: 0, fontSize: 10.5, lineHeight: 1.7, color: 'var(--muted-2)', textAlign: 'center' }}>
              By placing this order you agree to our{' '}
              <Link to="/terms" style={{ textDecoration: 'underline' }}>Terms &amp; Conditions</Link> and{' '}
              <Link to="/privacy" style={{ textDecoration: 'underline' }}>Privacy Policy</Link>.
            </p>
            <Link to="/cart" className="btn btn-sm" style={{ textAlign: 'center' }}>
              Back to bag
            </Link>
          </aside>
        </form>
      </section>
    </Shell>
  )
}

const AUTOCOMPLETE: Record<string, string> = {
  customerName: 'name',
  email: 'email',
  phone: 'tel',
  address1: 'address-line1',
  address2: 'address-line2',
  city: 'address-level2',
  postcode: 'postal-code',
  country: 'country-name',
}
